import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/projects', label: 'Projects' },
  { href: '/contact', label: 'Contact Us' },
];

const LOGO_URL = "https://customer-assets.emergentagent.com/job_maple-land-dev/artifacts/5i2dxxuh_koro%20living%20logo4.png";

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const isHomePage = location.pathname === '/';
  const headerBg = isScrolled || !isHomePage 
    ? 'glass-header border-b border-border/40' 
    : 'bg-transparent';
  const textColor = isScrolled || !isHomePage ? 'text-foreground' : 'text-white';
  
  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-40">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <img 
              src={LOGO_URL} 
              alt="Koro Living" 
              className={`h-36 w-auto ${isScrolled || !isHomePage ? 'invert' : 'mix-blend-screen'}`}
            />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium tracking-wide transition-colors hover:opacity-70 ${textColor} ${
                  location.pathname === link.href ? 'opacity-100' : 'opacity-80'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          
          {/* CTA Button - Desktop */}
          <div className="hidden md:block">
            <Button
              asChild
              className={`rounded-full px-6 ${
                isScrolled || !isHomePage 
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                  : 'bg-white text-black hover:bg-white/90'
              }`}
              data-testid="header-cta"
            >
              <Link to="/contact">Get In Touch</Link>
            </Button>
          </div>
          
          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className={textColor} data-testid="mobile-menu-trigger">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-80">
              <div className="flex flex-col gap-8 mt-8">
                <Link to="/" className="flex items-center gap-3" onClick={() => setMobileOpen(false)}>
                  <img src={LOGO_URL} alt="Koro Living" className="h-36 w-auto invert" />
                </Link>
                
                <nav className="flex flex-col gap-4">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileOpen(false)}
                      className={`text-lg font-medium py-2 transition-colors hover:text-muted-foreground ${
                        location.pathname === link.href ? 'text-foreground' : 'text-muted-foreground'
                      }`}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>
                
                <Button asChild className="rounded-full mt-4" data-testid="mobile-cta">
                  <Link to="/contact" onClick={() => setMobileOpen(false)}>
                    Get In Touch
                  </Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </motion.header>
  );
}
